import { getModel } from '../../model/registry'
import { useEffect, useState, useRef, forwardRef } from "react";
import { TextControl } from "../generic/Core";
import { FlexBox } from "../generic/Layout";
import useKeyPress from "../../hooks/useKeyPress";
import IconButton from "../generic/IconButton";
import { Icon_Search, Icon_Edit, Icon_Sync, Icon_Clear } from "../../styles/icons";
import { Spacer } from "../generic/Core";
import styled from "styled-components";
import ModalCard from "../modal/ModalCard";
import { useModalStack } from "../../context/modalStackContext";
import useApi from "../../hooks/useApi";
import generateEndpoint from "../../utils/generateEndpoint";

const InputContainer = styled.div`
    position: relative;
    width: 100%;
`

const IconContainer = styled.span`
    position: absolute;
    top: 0.25rem;
    right: ${({ $right }) => $right ? $right : '0.5rem'};
`

const ConnectInput = forwardRef(({ showModal, placeholder, valueName, name, isRed, autoComplete,
    testId, origin, entity, id, onSelect, onClear, sync, setSync, manualEntry, onManualEntry, labelId, inputId, onClose, emptyAdornment }, ref) => {
    const [isFocused, setIsFocused] = useState(false)
    const [isHovered, setIsHovered] = useState(false)
    const wrapperRef = useRef(null)
    const { getMe } = useApi();
    const mvConfig = getModel('modalCardData')[entity]
    const path = mvConfig?.path || entity
    const endpoint = generateEndpoint(path, origin?.id)

    const handleFocus = () => setIsFocused(true)
    const handleBlur = (e) => {
        if (!wrapperRef.current?.contains(e.relatedTarget)) {
            setIsFocused(false)
        }
    }

    const enterPressed = useKeyPress('Enter')
    const { pushModal } = useModalStack();

    const modalConfig = getModel('modalCardData')[entity]

    useEffect(() => {
        const focusedElem = document.activeElement;
        if (enterPressed && focusedElem.name === name) {
            showModal()
        }
    }, [enterPressed]);

    const activateSync = () => {
        if (sync === false) setSync(true)
    }

    const clickAction = () => {

        pushModal(ModalCard, {
            entity,
            origin,
            isVisible: true, // Always true when pushed to stack
            setIsVisible: () => { }, // No-op since we're using the stack
            modalTitle: modalConfig?.titleBar?.edit,
            mode: 'fullscreen',
            load: 'true',
            id,
            testId: `${testId}.modalView`,
            onClose: () => {
                // The modal stack will handle removing the modal
                if (onClose) onClose();
            },
            effect: activateSync,
            opacity: 0.5,
        });
    }

    const syncAction = async () => {
        const result = await getMe(`${endpoint}/${id}`)
        onSelect(result.data)
        setSync(!sync)
    }

    return (
        <div ref={wrapperRef} onFocus={handleFocus} onBlur={handleBlur} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} style={{ width: '100%' }}>
        <FlexBox >
            <InputContainer>
                <TextControl
                    ref={ref}
                    id={inputId || name}
                    value={valueName}
                    name={name}
                    placeholder={placeholder}
                    onChange={manualEntry ? (e) => onManualEntry(e.target.value) : (e) => {}}
                    autoComplete={autoComplete}
                    aria-labelledby={labelId || undefined}
                    {...(isRed && { required: true })}
                    {...(!manualEntry && { onClick: showModal })}
                    data-testid={testId}
                />
                {/* Shown over the input only while it is empty — Connect's emptyActions */}
                {!valueName && emptyAdornment}
                {/* Clear button - only show when there's a value and onClear is provided */}
                {(isFocused || isHovered) && valueName && onClear && <IconContainer $right={!sync && id && mvConfig ? '2.5rem' : '0.5rem'}>
                    <IconButton testId={`${testId}.clear`} $variant='secondary' clickAction={onClear} icon={Icon_Clear} ariaLabel='Clear selection' />
                </IconContainer>}
                {(isFocused || isHovered) && !sync && id && mvConfig && <IconContainer $right='0.5rem'>
                    <IconButton testId={`${testId}.edit`} $variant='transparent' clickAction={clickAction} icon={Icon_Edit} ariaLabel='Edit selected record' />
                </IconContainer>}
                {(isFocused || isHovered) && sync && <IconContainer $right={onClear && mvConfig ? '2.5rem' : '0.5rem'}>
                    <IconButton testId={`${testId}.sync`} $variant='transparent' clickAction={syncAction} icon={Icon_Sync} ariaLabel='Refresh selected record' />
                </IconContainer>}
            </InputContainer>
            <Spacer width={'0.5rem'} />
            <IconButton icon={Icon_Search} $marginRight={'0rem'} $rounded $variant='primary' $size='2.25rem' clickAction={showModal} ariaLabel='Open search' testId={`${testId}.search`} />
        </FlexBox>
        </div>
    );
});

export default ConnectInput;
